import { useCallback, useEffect, useRef, useState } from "react";

const W = 640;
const H = 400;
const HUD_H = 52;
const PLAY_TOP = HUD_H;
const PLAY_H = H - HUD_H;
const GAME_DURATION = 25;
const TARGET_SCORE = 20;

const BUG_TYPES = {
  common: {
    key: "common",
    color: "#E85D75",
    ring: "#141414",
    size: 28,
    speed: 70,
    hp: 1,
    points: 1,
    wobbleAmp: 14,
    wobbleFreq: 3,
    weight: 60,
    label: "🪲",
    displayName: "Bug",
  },
  fast: {
    key: "fast",
    color: "#F4C43E",
    ring: "#141414",
    size: 22,
    speed: 155,
    hp: 1,
    points: 2,
    wobbleAmp: 8,
    wobbleFreq: 6,
    weight: 25,
    label: "🦟",
    displayName: "Mosquito",
  },
  zigzag: {
    key: "zigzag",
    color: "#8C9EFF",
    ring: "#141414",
    size: 26,
    speed: 105,
    hp: 1,
    points: 3,
    wobbleAmp: 55,
    wobbleFreq: 4.5,
    weight: 12,
    label: "👾",
    displayName: "Glitch",
  },
  boss: {
    key: "boss",
    color: "#8AC98A",
    ring: "#141414",
    size: 46,
    speed: 42,
    hp: 3,
    points: 8,
    wobbleAmp: 12,
    wobbleFreq: 2,
    weight: 3,
    label: "🕷️",
    displayName: "Boss",
  },
};

const TOTAL_WEIGHT = Object.values(BUG_TYPES).reduce((s, b) => s + b.weight, 0);

function pickBugType() {
  let r = Math.random() * TOTAL_WEIGHT;
  for (const key of Object.keys(BUG_TYPES)) {
    r -= BUG_TYPES[key].weight;
    if (r <= 0) return BUG_TYPES[key];
  }
  return BUG_TYPES.common;
}

function createAudio() {
  let ctx = null;
  const ensure = () => {
    if (typeof window === "undefined") return null;
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    }
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  };
  const beep = ({ freq, duration, type = "sine", volume = 0.1, slide }) => {
    const audio = ensure();
    if (!audio) return;
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = type;
    const now = audio.currentTime;
    osc.frequency.setValueAtTime(freq, now);
    if (slide) osc.frequency.exponentialRampToValueAtTime(slide, now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(audio.destination);
    osc.start(now);
    osc.stop(now + duration);
  };
  return {
    resume: () => ensure(),
    shoot: () => beep({ freq: 880, slide: 440, duration: 0.06, type: "square", volume: 0.05 }),
    hit: () => beep({ freq: 240, slide: 90, duration: 0.15, type: "sawtooth", volume: 0.09 }),
    bossHit: () => beep({ freq: 140, slide: 60, duration: 0.28, type: "sawtooth", volume: 0.12 }),
    miss: () => beep({ freq: 130, duration: 0.05, type: "sine", volume: 0.04 }),
    combo: (n) => beep({ freq: 500 + n * 90, duration: 0.09, type: "triangle", volume: 0.08 }),
    tick: () => beep({ freq: 620, duration: 0.05, type: "sine", volume: 0.06 }),
    go: () => beep({ freq: 900, slide: 1400, duration: 0.25, type: "triangle", volume: 0.15 }),
    win: () => {
      beep({ freq: 500, duration: 0.12, type: "triangle", volume: 0.12 });
      setTimeout(() => beep({ freq: 700, duration: 0.14, type: "triangle", volume: 0.12 }), 120);
      setTimeout(() => beep({ freq: 950, duration: 0.28, type: "triangle", volume: 0.14 }), 260);
    },
    lose: () => beep({ freq: 220, slide: 90, duration: 0.5, type: "sawtooth", volume: 0.12 }),
  };
}

function spawnBug(elapsed) {
  const type = pickBugType();
  const difficulty = Math.min(1, elapsed / (GAME_DURATION * 0.8));
  const speedMul = 1 + difficulty * 0.6;

  const edge = Math.floor(Math.random() * 4);
  let x;
  let y;
  let angle;
  if (edge === 0) {
    x = -type.size;
    y = PLAY_TOP + type.size + Math.random() * (PLAY_H - type.size * 2);
    angle = -Math.PI / 8 + Math.random() * (Math.PI / 4);
  } else if (edge === 1) {
    x = W + type.size;
    y = PLAY_TOP + type.size + Math.random() * (PLAY_H - type.size * 2);
    angle = Math.PI + (-Math.PI / 8 + Math.random() * (Math.PI / 4));
  } else if (edge === 2) {
    x = type.size + Math.random() * (W - type.size * 2);
    y = PLAY_TOP - type.size;
    angle = Math.PI / 2 + (-Math.PI / 8 + Math.random() * (Math.PI / 4));
  } else {
    x = type.size + Math.random() * (W - type.size * 2);
    y = H + type.size;
    angle = -Math.PI / 2 + (-Math.PI / 8 + Math.random() * (Math.PI / 4));
  }

  const speed = type.speed * speedMul;
  return {
    id: Math.random(),
    type,
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    hp: type.hp,
    born: elapsed,
    hitFlash: 0,
    alive: true,
    dying: 0,
    scale: 0.6,
    phase: Math.random() * Math.PI * 2,
  };
}

function spawnParticles(state, x, y, color, count = 16, spread = 220) {
  for (let i = 0; i < count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 40 + Math.random() * spread;
    state.particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 40,
      life: 0.6 + Math.random() * 0.4,
      maxLife: 0.6 + Math.random() * 0.4,
      color,
      size: 2 + Math.random() * 4,
      gravity: 260,
    });
  }
}

function spawnPop(state, x, y, text, color) {
  state.pops.push({ x, y, text, color, life: 1.1, maxLife: 1.1, vy: -55 });
}

function shake(state, intensity) {
  state.shake.intensity = Math.max(state.shake.intensity, intensity);
  state.shake.duration = 0.35;
}

function comboMultiplier(combo) {
  if (combo >= 12) return 4;
  if (combo >= 8) return 3;
  if (combo >= 4) return 2;
  return 1;
}

function drawGrid(ctx, offset) {
  ctx.save();
  ctx.strokeStyle = "rgba(138, 201, 138, 0.08)";
  ctx.lineWidth = 1;
  const step = 24;
  const ox = offset % step;
  const oy = (offset * 0.6) % step;
  for (let x = -step + ox; x < W + step; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, PLAY_TOP);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = PLAY_TOP - step + oy; y < H + step; y += step) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  ctx.restore();
}

function drawHUD(ctx, state) {
  ctx.save();
  ctx.fillStyle = "#0d0d0d";
  ctx.fillRect(0, 0, W, HUD_H);
  ctx.strokeStyle = "#141414";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, HUD_H);
  ctx.lineTo(W, HUD_H);
  ctx.stroke();

  const timeLeft = Math.max(0, state.timeLeft);
  const urgency = timeLeft < 5 ? "#E85D75" : timeLeft < 10 ? "#F4C43E" : "#8AC98A";
  ctx.font = "700 15px 'Space Grotesk', system-ui";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillStyle = urgency;
  ctx.fillText(`⏱ ${timeLeft.toFixed(1)}s`, 16, HUD_H / 2 - 6);

  const barX = 16;
  const barY = HUD_H - 12;
  const barW = 160;
  ctx.fillStyle = "rgba(255,255,255,0.1)";
  ctx.fillRect(barX, barY, barW, 4);
  ctx.fillStyle = urgency;
  ctx.fillRect(barX, barY, barW * (timeLeft / GAME_DURATION), 4);

  ctx.textAlign = "center";
  ctx.fillStyle = "#F7F1E8";
  ctx.font = "800 22px 'Archivo Black', system-ui";
  ctx.fillText(`${state.score} / ${TARGET_SCORE}`, W / 2, HUD_H / 2 - 4);
  ctx.font = "500 11px 'Space Grotesk', system-ui";
  ctx.fillStyle = "rgba(247,241,232,0.5)";
  ctx.fillText("SCORE", W / 2, HUD_H / 2 + 14);

  const mult = comboMultiplier(state.combo);
  ctx.textAlign = "right";
  ctx.font = "800 18px 'Archivo Black', system-ui";
  ctx.fillStyle = mult > 1 ? "#F4C43E" : "#F7F1E8";
  ctx.fillText(`🔥 x${mult}`, W - 16, HUD_H / 2 - 6);
  ctx.font = "500 11px 'Space Grotesk', system-ui";
  ctx.fillStyle = "rgba(247,241,232,0.5)";
  ctx.fillText(`combo ${state.combo}`, W - 16, HUD_H / 2 + 12);
  ctx.restore();
}

function drawBug(ctx, bug, t) {
  const s = bug.type.size * (1 + bug.hitFlash * 0.15) * bug.scale;
  ctx.save();
  ctx.translate(bug.x, bug.y);

  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(0, s * 0.7, s * 0.9, s * 0.25, 0, 0, Math.PI * 2);
  ctx.fill();

  if (bug.type.key === "boss") {
    const pulse = 0.5 + 0.5 * Math.sin(t * 4);
    ctx.strokeStyle = `rgba(232, 93, 117, ${0.35 + pulse * 0.35})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, s * 1.25 + pulse * 4, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.fillStyle = bug.hitFlash > 0.2 ? "#FFFFFF" : bug.type.color;
  ctx.strokeStyle = bug.type.ring;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(0, 0, s, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.font = `${s * 1.05}px system-ui, "Segoe UI Emoji"`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(bug.type.label, 0, 2);

  if (bug.type.hp > 1) {
    const ratio = bug.hp / bug.type.hp;
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillRect(-s, s + 4, s * 2, 5);
    ctx.fillStyle = ratio > 0.5 ? "#8AC98A" : ratio > 0.25 ? "#F4C43E" : "#E85D75";
    ctx.fillRect(-s, s + 4, s * 2 * ratio, 5);
    ctx.strokeStyle = "#141414";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(-s, s + 4, s * 2, 5);
  }

  ctx.restore();
}

function drawCrosshair(ctx, x, y, active) {
  if (y < PLAY_TOP) return;
  ctx.save();
  ctx.translate(x, y);
  const size = active ? 22 : 16;
  ctx.strokeStyle = active ? "#F4C43E" : "#F7F1E8";
  ctx.lineWidth = active ? 3 : 2;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-size - 6, 0);
  ctx.lineTo(-size + 4, 0);
  ctx.moveTo(size - 4, 0);
  ctx.lineTo(size + 6, 0);
  ctx.moveTo(0, -size - 6);
  ctx.lineTo(0, -size + 4);
  ctx.moveTo(0, size - 4);
  ctx.lineTo(0, size + 6);
  ctx.stroke();
  ctx.fillStyle = active ? "#F4C43E" : "#F7F1E8";
  ctx.beginPath();
  ctx.arc(0, 0, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function makeState() {
  return {
    phase: "ready",
    countdown: 3,
    countdownTimer: 0,
    elapsed: 0,
    timeLeft: GAME_DURATION,
    score: 0,
    combo: 0,
    maxCombo: 0,
    shots: 0,
    hits: 0,
    bugs: [],
    particles: [],
    pops: [],
    flashes: [],
    cursor: { x: W / 2, y: PLAY_TOP + PLAY_H / 2, hovering: false, active: false },
    lastSpawn: 0,
    spawnInterval: 1200,
    shake: { intensity: 0, duration: 0, x: 0, y: 0 },
    lastTime: 0,
    scrollOffset: 0,
  };
}

export function BugHuntGame({ onVictory, onQuit }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(makeState());
  const rafRef = useRef(null);
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const [phase, setPhase] = useState("ready");
  const [summary, setSummary] = useState({ score: 0, hits: 0, shots: 0, maxCombo: 0 });

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const play = useCallback((sound, ...args) => {
    if (mutedRef.current) return;
    if (!audioRef.current) audioRef.current = createAudio();
    const fn = audioRef.current[sound];
    if (fn) fn(...args);
  }, []);

  const finish = useCallback((result) => {
    const s = stateRef.current;
    s.phase = "ending";
    setSummary({ score: s.score, hits: s.hits, shots: s.shots, maxCombo: s.maxCombo });
    setPhase(result);
    if (result === "won") play("win");
    else play("lose");
  }, [play]);

  const startGame = useCallback(() => {
    if (!audioRef.current) audioRef.current = createAudio();
    audioRef.current?.resume?.();
    const fresh = makeState();
    fresh.phase = "ready";
    stateRef.current = fresh;
    setPhase("ready");
    setSummary({ score: 0, hits: 0, shots: 0, maxCombo: 0 });
  }, []);

  const step = useCallback(
    (timestamp) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const ctx = canvas.getContext("2d");
      const state = stateRef.current;

      const last = state.lastTime || timestamp;
      const dt = Math.min((timestamp - last) / 1000, 0.05);
      state.lastTime = timestamp;
      state.scrollOffset += dt * 18;

      if (state.phase === "ready") {
        state.countdownTimer += dt;
        if (state.countdownTimer >= 1) {
          state.countdownTimer = 0;
          state.countdown -= 1;
          if (state.countdown > 0) {
            play("tick");
          } else if (state.countdown === 0) {
            play("go");
            state.phase = "playing";
            state.lastSpawn = state.spawnInterval;
            for (let i = 0; i < 3; i += 1) {
              state.bugs.push(spawnBug(0));
            }
            setPhase("playing");
          }
        }
      } else if (state.phase === "playing") {
        state.elapsed += dt;
        state.timeLeft = Math.max(0, GAME_DURATION - state.elapsed);

        const progress = Math.min(1, state.elapsed / GAME_DURATION);
        state.spawnInterval = 1200 - progress * 700;

        state.lastSpawn += dt * 1000;
        if (state.lastSpawn >= state.spawnInterval && state.bugs.length < 9) {
          state.lastSpawn = 0;
          state.bugs.push(spawnBug(state.elapsed));
        }

        for (const bug of state.bugs) {
          if (!bug.alive) {
            bug.dying = Math.max(0, bug.dying - dt);
            bug.scale = Math.max(0, bug.scale - dt * 5);
            continue;
          }
          bug.scale = Math.min(1, bug.scale + dt * 6);
          bug.phase += dt * bug.type.wobbleFreq;
          const perpX = -bug.vy;
          const perpY = bug.vx;
          const perpLen = Math.hypot(perpX, perpY) || 1;
          const wobble = Math.sin(bug.phase) * bug.type.wobbleAmp * dt;
          bug.x += bug.vx * dt + (perpX / perpLen) * wobble;
          bug.y += bug.vy * dt + (perpY / perpLen) * wobble;
          bug.hitFlash = Math.max(0, bug.hitFlash - dt * 4);
        }

        state.bugs = state.bugs.filter((b) => {
          if (!b.alive) return b.scale > 0.02;
          const margin = b.type.size * 3;
          const escaped =
            b.x < -margin || b.x > W + margin || b.y < PLAY_TOP - margin || b.y > H + margin;
          if (escaped) {
            if (state.combo > 0) play("miss");
            state.combo = 0;
            return false;
          }
          return true;
        });

        if (state.timeLeft <= 0) {
          finish(state.score >= TARGET_SCORE ? "won" : "lost");
        } else if (state.score >= TARGET_SCORE) {
          finish("won");
        }
      }

      for (const p of state.particles) {
        p.vy += p.gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life -= dt;
      }
      state.particles = state.particles.filter((p) => p.life > 0);

      for (const pop of state.pops) {
        pop.y += pop.vy * dt;
        pop.vy += 20 * dt;
        pop.life -= dt;
      }
      state.pops = state.pops.filter((p) => p.life > 0);

      for (const f of state.flashes) {
        f.life -= dt;
      }
      state.flashes = state.flashes.filter((f) => f.life > 0);

      if (state.shake.duration > 0) {
        state.shake.duration -= dt;
        const s = state.shake.intensity * (state.shake.duration / 0.35);
        state.shake.x = (Math.random() - 0.5) * s;
        state.shake.y = (Math.random() - 0.5) * s;
        if (state.shake.duration <= 0) {
          state.shake.x = 0;
          state.shake.y = 0;
          state.shake.intensity = 0;
        }
      }

      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== W * dpr) {
        canvas.width = W * dpr;
        canvas.height = H * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      ctx.save();
      ctx.translate(state.shake.x, state.shake.y);

      ctx.fillStyle = "#141414";
      ctx.fillRect(0, PLAY_TOP, W, PLAY_H);
      drawGrid(ctx, state.scrollOffset);

      const cursor = state.cursor;
      let hoveringAny = false;
      for (const bug of state.bugs) {
        if (!bug.alive) continue;
        const d = Math.hypot(bug.x - cursor.x, bug.y - cursor.y);
        if (d <= bug.type.size + 6) hoveringAny = true;
      }

      for (const f of state.flashes) {
        const t = f.life / f.maxLife;
        const r = 30 + (1 - t) * 40;
        const grad = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, r);
        grad.addColorStop(0, `rgba(244, 196, 62, ${0.6 * t})`);
        grad.addColorStop(1, "rgba(244, 196, 62, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const bug of state.bugs) {
        drawBug(ctx, bug, state.elapsed);
      }

      for (const p of state.particles) {
        const alpha = Math.max(0, p.life / p.maxLife);
        ctx.fillStyle = p.color.replace("ALPHA", alpha.toFixed(2));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const pop of state.pops) {
        const alpha = Math.max(0, pop.life / pop.maxLife);
        ctx.font = "800 18px 'Archivo Black', system-ui";
        ctx.textAlign = "center";
        ctx.fillStyle = pop.color.replace("ALPHA", alpha.toFixed(2));
        ctx.strokeStyle = `rgba(20, 20, 20, ${alpha})`;
        ctx.lineWidth = 3;
        ctx.strokeText(pop.text, pop.x, pop.y);
        ctx.fillText(pop.text, pop.x, pop.y);
      }

      drawCrosshair(ctx, cursor.x, cursor.y, hoveringAny);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(0, PLAY_TOP, W, PLAY_H);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        if (state.countdown > 0) {
          ctx.font = "900 92px 'Archivo Black', system-ui";
          ctx.fillStyle = "#F4C43E";
          ctx.fillText(String(state.countdown), W / 2, PLAY_TOP + PLAY_H / 2);
          ctx.font = "600 14px 'Space Grotesk', system-ui";
          ctx.fillStyle = "#F7F1E8";
          ctx.fillText("Get ready", W / 2, PLAY_TOP + PLAY_H / 2 + 60);
        } else {
          ctx.font = "900 72px 'Archivo Black', system-ui";
          ctx.fillStyle = "#8AC98A";
          ctx.fillText("GO!", W / 2, PLAY_TOP + PLAY_H / 2);
        }
      }

      ctx.restore();

      drawHUD(ctx, state);

      rafRef.current = requestAnimationFrame(step);
    },
    [finish, play]
  );

  useEffect(() => {
    startGame();
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [startGame, step]);

  const eventToCanvas = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * W;
    const y = ((event.clientY - rect.top) / rect.height) * H;
    return { x, y };
  };

  const handleMove = (event) => {
    const { x, y } = eventToCanvas(event);
    stateRef.current.cursor.x = x;
    stateRef.current.cursor.y = y;
  };

  const handleShoot = (event) => {
    const state = stateRef.current;
    if (state.phase !== "playing") return;
    const { x, y } = eventToCanvas(event);
    if (y < PLAY_TOP) return;

    state.shots += 1;
    play("shoot");
    state.flashes.push({ x, y, life: 0.25, maxLife: 0.25 });

    let hit = null;
    let hitDist = Infinity;
    for (const bug of state.bugs) {
      if (!bug.alive) continue;
      const d = Math.hypot(bug.x - x, bug.y - y);
      if (d <= bug.type.size + 6 && d < hitDist) {
        hit = bug;
        hitDist = d;
      }
    }

    if (!hit) {
      state.combo = 0;
      shake(state, 4);
      spawnParticles(
        state,
        x,
        y,
        "rgba(247, 241, 232, ALPHA)",
        6,
        60
      );
      return;
    }

    state.hits += 1;
    hit.hp -= 1;
    hit.hitFlash = 1;
    shake(state, hit.type.key === "boss" ? 10 : 6);

    if (hit.hp <= 0) {
      state.combo += 1;
      state.maxCombo = Math.max(state.maxCombo, state.combo);
      const mult = comboMultiplier(state.combo);
      const gained = hit.type.points * mult;
      state.score += gained;
      hit.alive = false;
      hit.dying = 0.3;
      spawnParticles(
        state,
        hit.x,
        hit.y,
        `rgba(${hexRgb(hit.type.color)}, ALPHA)`,
        hit.type.key === "boss" ? 28 : 18,
        hit.type.key === "boss" ? 320 : 200
      );
      const popColor = mult > 1 ? "rgba(244, 196, 62, ALPHA)" : "rgba(247, 241, 232, ALPHA)";
      spawnPop(state, hit.x, hit.y - hit.type.size, `+${gained}`, popColor);
      if (mult > 1 && state.combo % 4 === 0) {
        spawnPop(
          state,
          hit.x,
          hit.y - hit.type.size - 22,
          `COMBO x${mult}!`,
          "rgba(232, 93, 117, ALPHA)"
        );
        play("combo", mult);
      }
      if (hit.type.key === "boss") play("bossHit");
      else play("hit");
    } else {
      spawnParticles(
        state,
        hit.x,
        hit.y,
        `rgba(${hexRgb(hit.type.color)}, ALPHA)`,
        8,
        140
      );
      play("bossHit");
    }
  };

  const accuracy = summary.shots ? Math.round((summary.hits / summary.shots) * 100) : 0;

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="m-0 font-display text-[15px] text-ink">
          🎯 BUG HUNT · reach {TARGET_SCORE} points in {GAME_DURATION}s
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="cursor-pointer rounded-lg border-2 border-ink bg-white px-2.5 py-1 text-[11px] font-bold text-ink transition-transform hover:-translate-y-0.5"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? "🔇 muted" : "🔊 sound"}
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-[14px] border-3 border-ink bg-ink">
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          onPointerMove={handleMove}
          onPointerDown={handleShoot}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            aspectRatio: `${W} / ${H}`,
            cursor: "none",
            touchAction: "none",
          }}
        />

        {(phase === "won" || phase === "lost") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink/85 p-6 text-center">
            <p className="text-[36px]">{phase === "won" ? "🎯🏆" : "💥"}</p>
            <h3 className="font-display text-2xl text-cream">
              {phase === "won" ? "Nice shooting! Case studies unlocked." : "Out of time. Bugs win."}
            </h3>
            <div className="grid grid-cols-4 gap-4 rounded-xl border-2 border-cream/30 bg-black/40 px-5 py-3 text-cream">
              <Stat label="Score" value={summary.score} />
              <Stat label="Hits" value={summary.hits} />
              <Stat label="Accuracy" value={`${accuracy}%`} />
              <Stat label="Max combo" value={summary.maxCombo} />
            </div>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {phase === "won" ? (
                <button
                  type="button"
                  onClick={onVictory}
                  className="cursor-pointer rounded-[10px] border-3 border-ink bg-yellow px-[22px] py-3 font-display text-sm text-ink transition-transform hover:-translate-y-0.5"
                >
                  Claim it →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startGame}
                  className="cursor-pointer rounded-[10px] border-3 border-ink bg-yellow px-[22px] py-3 font-display text-sm text-ink transition-transform hover:-translate-y-0.5"
                >
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={onQuit}
                className="cursor-pointer rounded-[10px] border-2 border-cream/40 bg-transparent px-[18px] py-3 font-display text-[13px] text-cream/80 hover:text-cream"
              >
                back to question
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-ink">
        <div className="flex flex-wrap gap-3">
          <Legend color="bg-pink" label="Bug · 1pt" />
          <Legend color="bg-yellow" label="Mosquito · 2pt" />
          <Legend color="bg-periwinkle" label="Glitch · 3pt" />
          <Legend color="bg-green" label="Boss · 8pt (3 hits)" />
        </div>
        <p className="m-0 font-bold">Miss = combo resets. Bosses = huge points.</p>
      </div>

      {phase !== "won" && phase !== "lost" && (
        <button
          type="button"
          onClick={onQuit}
          className="mt-4 cursor-pointer border-none bg-transparent text-xs font-bold text-ink underline opacity-70"
        >
          back to the question
        </button>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-display text-xl text-cream">{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-cream/60">{label}</span>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block h-3 w-3 rounded-full border-2 border-ink ${color}`} />
      <span>{label}</span>
    </div>
  );
}

function hexRgb(hex) {
  const h = hex.replace("#", "");
  const num = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
}

BugHuntGame.dimensions = { W, H, TARGET_SCORE, GAME_DURATION };
